import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import ReaderPortal from './ReaderPortal';
import LandingPage from './pages/LandingPage';
import SelectAuthorPage from './pages/SelectAuthorPage';
import PublishingRoadmap from './PublishingChecklist_Product';
import PlotPlanner from './PlotPlanner_Product';
import MarketingCompass from './marketing/MarketingCompass';
import ToolkitHome from './toolkit/ToolkitHome';
import AdminRoute from './admin/AdminRoute';
import PurchaseGate from './PurchaseGate';
import PreOrderGate from './PreOrderGate';
import ErrorBoundary from './ErrorBoundary';
import { AuthProvider } from './context/AuthContext';
import AuthorSignup from './author/AuthorSignup';
import AuthorLogin from './author/AuthorLogin';
import AuthorDashboard from './author/AuthorDashboard';
import VerifyEmail from './author/VerifyEmail';
import WelcomeScreen from './WelcomeScreen';
import GuestStorefront from './GuestStorefront';
import AuthorStore from './AuthorStore';
import ShopPage from './pages/ShopPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DeleteAccount from './pages/DeleteAccount';
import ListeningApp from './listening/ListeningApp';
import HomeScreen from './HomeScreen';
import NativeReaderNotice from './components/NativeReaderNotice';
import { Capacitor } from '@capacitor/core';
import { initPushNotifications } from './lib/pushNotifications';

// Initialise push notifications on native (Capacitor Android)
initPushNotifications().catch(err => {
  console.warn('[Push] Init failed:', err);
});

function Router() {
  const path = window.location.pathname;

  // Privacy policy (public, no auth required — needed for Google Play)
  if (path === '/privacy') {
    return <PrivacyPolicy />;
  }

  // Account & data deletion (public, no auth required — needed for Google Play)
  if (path === '/delete-account') {
    return <DeleteAccount />;
  }

  // TEMPORARY: visual-review route for the listening app. The shell shows the
  // sign-in screen when signed out and the reader's library once signed in, so
  // the preview goes from sign-in straight to the library. Removed once the
  // listening app is wired into the native entry.
  if (path === '/signin-preview') {
    return <ListeningApp />;
  }

  // TEMPORARY: visual-review route for the branded home page. Static look only —
  // no navigation or sign-in wiring yet. Removed once the home page is wired into
  // the real flow in a later step.
  if (path === '/home-preview') {
    return <HomeScreen />;
  }

  // ── Native app is reader-only ───────────────────────────────────────────────
  // The Play app lets readers LISTEN to audiobooks they already have; ALL buying
  // happens on the web. So in native we never render a store, checkout, pre-order,
  // ebook-buy, or reader-portal surface — a reviewer cannot reach a Buy/Rent CTA
  // by any route. The listening app (/signin-preview), the audiobook player
  // (/listen/*, where PurchaseGate hides the rent CTA in native), the public legal
  // pages, and the home screen stay available. WEB IS COMPLETELY UNCHANGED.
  if (Capacitor.isNativePlatform()) {
    const nativeBlocked =
      path === '/shop' ||
      path === '/store' || path === '/guest' ||
      path.startsWith('/store/') ||
      path.startsWith('/read/') ||
      path.startsWith('/preorder/') ||
      path === '/welcome' || path === '/join' ||
      path.startsWith('/reader') ||
      path.startsWith('/author');
    if (nativeBlocked) return <NativeReaderNotice />;
  }

  // Welcome / role picker
  if (path === '/welcome' || path === '/join') {
    return <WelcomeScreen />;
  }

  // Author routes
  if (path === '/author/signup') {
    return <AuthorSignup />;
  }

  if (path === '/author/verify-email') {
    return <VerifyEmail />;
  }

  if (path === '/author/login') {
    return <AuthorLogin />;
  }

  if (path.startsWith('/author/dashboard') || path === '/author') {
    return <AuthorDashboard />;
  }

  // Pre-order gates
  const preOrderEbookMatch = path.match(/^\/preorder\/ebook\/([^/]+)/);
  if (preOrderEbookMatch) {
    return <PreOrderGate contentType="ebook" contentId={preOrderEbookMatch[1]} />;
  }
  const preOrderAudiobookMatch = path.match(/^\/preorder\/audiobook\/([^/]+)/);
  if (preOrderAudiobookMatch) {
    return <PreOrderGate contentType="audiobook" contentId={preOrderAudiobookMatch[1]} />;
  }

  // Audiobook player (purchase gate). The two-segment form carries the authorId so a
  // signed-out reader can load the audiobook via a direct doc read (the per-doc rule
  // allows it) and reach the rent card. It must be checked BEFORE the single-segment
  // form, otherwise that one would capture the authorId as the audiobookId.
  const listenAuthorMatch = path.match(/^\/listen\/([^/]+)\/([^/]+)/);
  if (listenAuthorMatch) {
    return (
      <PurchaseGate
        contentType="audiobook"
        contentId={listenAuthorMatch[2]}
        contentAuthorId={listenAuthorMatch[1]}
      />
    );
  }
  const listenMatch = path.match(/^\/listen\/([^/]+)/);
  if (listenMatch) {
    return <PurchaseGate contentType="audiobook" contentId={listenMatch[1]} />;
  }

  // eBook reader (purchase gate)
  const readMatch = path.match(/^\/read\/([^/]+)/);
  if (readMatch) {
    return <PurchaseGate contentType="ebook" contentId={readMatch[1]} />;
  }

  // New multi-step shop (no portal account required)
  if (path === '/shop') {
    return <ShopPage />;
  }

  // Guest storefront
  if (path === '/store' || path === '/guest') {
    return <GuestStorefront />;
  }
  const storeMatch = path.match(/^\/store\/([^/]+)/);
  if (storeMatch) {
    return <AuthorStore authorId={storeMatch[1]} />;
  }

  // Reader landing page (new entry point)
  if (path === '/reader') {
    return <LandingPage />;
  }

  // Reader author selection (intermediate step between landing and portal)
  if (path === '/reader/select-author') {
    return <SelectAuthorPage />;
  }

  // Reader portal (existing flow)
  if (path === '/reader/portal') {
    return <ReaderPortal />;
  }

  // Reader shop — new multi-step guest flow.
  // Supports optional author/book scoping via shareable deep links:
  //   /reader/shop                         → full shop (all authors)
  //   /reader/shop/:authorSlug             → only that author's books
  //   /reader/shop/:authorSlug/:bookSlug   → straight to that book's product page
  // An unknown authorSlug falls back to the full shop (handled inside ShopPage).
  const shopMatch = path.match(/^\/reader\/shop(?:\/([^/]+))?(?:\/([^/]+))?\/?$/);
  if (shopMatch) {
    const params = new URLSearchParams(window.location.search);
    return (
      <ShopPage
        authorSlug={shopMatch[1] ? decodeURIComponent(shopMatch[1]) : null}
        bookSlug={shopMatch[2] ? decodeURIComponent(shopMatch[2]) : null}
        format={params.get('format')}
      />
    );
  }

  // Publishing roadmap (standalone product)
  if (path === '/publishing-roadmap') {
    return <PublishingRoadmap />;
  }

  // The Plot Planner (standalone product — same licence gate + account-synced
  // saving as the roadmap; accepts ?checkout=success|cancel like the roadmap).
  if (path === '/plot-planner') {
    return <PlotPlanner />;
  }

  // Marketing Compass (included with a Roadmap or Bundle licence — no separate
  // purchase). The route stays generic so the display name can change freely.
  // Gated twice: the feature flag + licence check here, and again server-side
  // in every marketing callable.
  if (path === '/marketing') {
    return <MarketingCompass />;
  }

  // Superadmin CRM — moved off the root to /admin (2026-07-29) so the root
  // could become the commercial toolkit's front door. Route-guarded: a
  // non-superadmin who finds this URL gets the TOOLKIT sign-in, never ARC's.
  if (path === '/admin' || path.startsWith('/admin/')) {
    return <AdminRoute />;
  }

  // Native app: open straight onto the branded home page (the reader entry).
  // Scoped to native only, and checked BEFORE the web root below so the Android
  // reader app never lands on the toolkit. WelcomeScreen's role picker is not
  // lost; it stays reachable at /welcome and /join.
  if (Capacitor.isNativePlatform()) {
    return <HomeScreen />;
  }

  // ── THE ROOT: the commercial toolkit's front door (World 1) ──
  // Signed out → Narratoria sign-in. Signed in → the three-card hub. This is
  // where www.narratoria.com sends people, and it never shows an ARC or reader
  // surface to anyone, superadmin included.
  return <ToolkitHome />;
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <AuthProvider>
        <Router />
      </AuthProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
